import { alpha, createTheme, PaletteOptions } from '@mui/material/styles';

/**
 * Theme options typed exactly as `createTheme` takes them, and their component
 * overrides. Building the theme in these types keeps TypeScript from comparing
 * two enormous MUI style types — that comparison alone outgrew the build's
 * type-check memory.
 */
export type ThemeInput = NonNullable<Parameters<typeof createTheme>[0]>;
export type ComponentStyles = NonNullable<ThemeInput['components']>;

/**
 * Refined dark: layered surfaces, soft off-white text, and one calm blue kept
 * for actions and "you are here". Colours are tuned to read on dark rather
 * than glare. It comes in tones, picked in the top bar's Appearance menu.
 *
 * Surfaces step up in lightness as they come forward:
 *   rail (sidebar) → canvas (page) → surface (cards, tables) → raised (dialogs,
 *   drawers, menus) → overlay (tooltips)
 *
 * Every dark colour in the app comes from these blocks.
 */
export interface DarkTone {
  label: string;
  /** One line for the Appearance menu. */
  hint: string;
  /** The sidebar's shade when this is SIDEBAR_TONE: the deepest of the set. */
  rail: string;
  canvas: string;
  surface: string;
  raised: string;
  overlay: string;
  border: string;
  hairline: string;
  /** Behind dialogs and drawers. */
  scrim: string;
  /** Inside text fields. */
  inputFill: string;
  /** A faint light at the top of the page, the canvas's only ornament. */
  glow: string;
  text: { primary: string; secondary: string; disabled: string; icon: string };
  accent: { main: string; light: string; dark: string };
  /** The tone's tint at an opacity, for hovers, fills and outlines. */
  veil: (opacity: number) => string;
}

export const DARK_TONES = {
  graphite: {
    label: 'Graphite',
    hint: 'Neutral charcoal',
    rail: '#0e0e10',
    canvas: '#131315',
    surface: '#19191c',
    raised: '#1f1f23',
    overlay: '#2a2a2f',
    border: 'rgba(255, 255, 255, 0.08)',
    hairline: 'rgba(255, 255, 255, 0.05)',
    scrim: 'rgba(0, 0, 0, 0.62)',
    inputFill: 'rgba(0, 0, 0, 0.18)',
    glow: 'radial-gradient(1100px 380px at 60% -120px, rgba(255, 255, 255, 0.035), transparent 70%)',
    text: { primary: '#ececee', secondary: '#a1a1a9', disabled: '#64646c', icon: '#b4b4bb' },
    accent: { main: '#5683dd', light: '#93b0ec', dark: '#3f6bc4' },
    // White reads brighter than slate, so it is used at a lighter touch.
    veil: (opacity: number) => `rgba(255, 255, 255, ${+(opacity * 0.7).toFixed(3)})`,
  },
  midnight: {
    label: 'Midnight',
    hint: 'Deep blue night',
    rail: '#06152a',
    canvas: '#0b1020',
    surface: '#111829',
    raised: '#161f33',
    overlay: '#1e2940',
    border: 'rgba(148, 163, 184, 0.14)',
    hairline: 'rgba(148, 163, 184, 0.09)',
    scrim: 'rgba(3, 6, 18, 0.66)',
    inputFill: 'rgba(3, 6, 18, 0.22)',
    glow: 'radial-gradient(1100px 380px at 60% -120px, rgba(76, 141, 255, 0.09), transparent 70%)',
    text: { primary: '#e6ebf4', secondary: '#9aa6bc', disabled: '#5f6b82', icon: '#aab5c8' },
    accent: { main: '#4c8dff', light: '#8ab4ff', dark: '#2f6fe0' },
    veil: (opacity: number) => `rgba(148, 163, 184, ${opacity})`,
  },
} satisfies Record<string, DarkTone>;

export type DarkToneName = keyof typeof DARK_TONES;
export const DEFAULT_DARK_TONE: DarkToneName = 'graphite';
/** The sidebar's tone in dark mode, whatever tone the pages use (light mode: theme/rail.ts). */
export const SIDEBAR_TONE: DarkToneName = 'midnight';
export const isDarkToneName = (v: unknown): v is DarkToneName =>
  typeof v === 'string' && v in DARK_TONES;

export const refinedDarkPalette = (t: DarkTone): PaletteOptions => ({
  mode: 'dark',
  primary: { ...t.accent, contrastText: '#ffffff' },
  secondary: { main: '#f472b6', light: '#f9a8d4', dark: '#db2777' },
  success: { main: '#34d399', light: '#6ee7b7', dark: '#059669' },
  warning: { main: '#fbbf24', light: '#fcd34d', dark: '#d97706' },
  error: { main: '#f87171', light: '#fca5a5', dark: '#dc2626' },
  info: { main: '#60a5fa', light: '#93c5fd', dark: '#2563eb' },
  background: { default: t.canvas, paper: t.surface },
  text: { primary: t.text.primary, secondary: t.text.secondary, disabled: t.text.disabled },
  divider: t.border,
  action: {
    active: t.text.icon,
    hover: t.veil(0.08),
    selected: alpha(t.accent.main, 0.16),
    focus: t.veil(0.14),
    disabled: t.veil(0.38),
    disabledBackground: t.veil(0.12),
  },
});

type StatusColor = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
const isStatusColor = (c: unknown): c is StatusColor =>
  typeof c === 'string' && ['primary', 'secondary', 'success', 'warning', 'error', 'info'].includes(c);

export const refinedDarkComponents = (t: DarkTone): ComponentStyles => {
  const raisedPaper = {
    backgroundColor: t.raised,
    backgroundImage: 'none',
    border: `1px solid ${t.border}`,
    boxShadow: `0 24px 48px ${alpha('#000', 0.45)}, 0 2px 6px ${alpha('#000', 0.3)}`,
  };

  return {
    MuiPaper: {
      styleOverrides: {
        // No elevation overlay: surfaces get their depth from colour and border.
        root: { borderRadius: 12, backgroundImage: 'none' },
        outlined: { borderColor: t.border },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${t.border}`,
          backgroundImage: `linear-gradient(180deg, ${t.veil(0.035)} 0%, transparent 120px)`,
          boxShadow: `0 1px 2px ${alpha('#000', 0.35)}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: raisedPaper },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { ...raisedPaper, borderRadius: 0, borderTop: 0, borderBottom: 0 },
      },
    },
    MuiPopover: {
      styleOverrides: { paper: raisedPaper },
    },
    MuiMenu: {
      styleOverrides: { paper: raisedPaper },
    },
    MuiAutocomplete: {
      styleOverrides: { paper: raisedPaper },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: t.scrim,
            backdropFilter: 'blur(3px)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: t.overlay,
          color: t.text.primary,
          border: `1px solid ${t.border}`,
          boxShadow: `0 8px 20px ${alpha('#000', 0.4)}`,
        },
        arrow: { color: t.overlay },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: t.hairline },
        head: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontWeight: 600,
          backgroundColor: t.veil(0.035),
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: t.veil(0.05) },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: t.inputFill,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.veil(0.2) },
          '&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': { borderColor: t.veil(0.36) },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: `0 4px 14px ${alpha('#000', 0.35)}` },
        },
        outlined: ({ ownerState }) =>
          ownerState.color === 'inherit' || ownerState.color === undefined
            ? { borderColor: t.veil(0.28) }
            : {},
      },
    },
    MuiChip: {
      styleOverrides: {
        // Coloured chips sit on a soft wash of their colour, text in its light tone.
        root: ({ ownerState, theme }) => {
          if (!isStatusColor(ownerState.color)) return {};
          const tone = theme.palette[ownerState.color];
          return ownerState.variant === 'outlined'
            ? { backgroundColor: alpha(tone.main, 0.1), borderColor: alpha(tone.main, 0.3), color: tone.light }
            : { backgroundColor: alpha(tone.main, 0.18), color: tone.light };
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const key = ownerState.severity ?? 'success';
          if (ownerState.variant === 'filled' || !isStatusColor(key)) return {};
          const tone = theme.palette[key];
          return {
            backgroundColor: alpha(tone.main, 0.08),
            border: `1px solid ${alpha(tone.main, 0.22)}`,
            color: theme.palette.text.primary,
            '& .MuiAlert-icon': { color: tone.main },
          };
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, borderRadius: 2 },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: t.border } },
    },
  };
};
